import { Contentful } from '../../types/contentful'
import { isObject, path } from '../../utils/obj'
import { currencyFormat, percentageFormat } from '../../utils/formatter'
import { getArrayValue } from '../../utils/product-schema'
import { getLinkAndText } from '../../utils/get-link-from-array'

/**
 * The type of the value, either an absolute value 'value', or a reference value 'valueFrom'.
 *
 * @typedef {string|null} ValueType
 */

/**
 * The value, either an absolute value, or a reference value.
 *
 * @typedef {*} Value
 */

/**
 * Takes an object slice from the schema, and finds whether the slice is a 'value' or 'valueFrom'
 * type.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @returns {[ValueType, Value]}
 */

export const getValueType = (
  slice: Contentful.ValueFromType,
): [string | null, Contentful.GetValueResultType] => {
  let result: [string | null, Contentful.GetValueResultType] = [null, null]
  if (!isObject(slice)) {
    return result
  }

  if ({}.hasOwnProperty.call(slice, 'valueFrom')) {
    result = ['valueFrom', slice.valueFrom!]
  } else if ({}.hasOwnProperty.call(slice, 'value')) {
    result = ['value', slice.value!]
  } else if ({}.hasOwnProperty.call(slice, 'valueCalculated')) {
    result = ['valueCalculated', slice.valueCalculated!]
  } else if ({}.hasOwnProperty.call(slice, 'compound')) {
    result = ['compound', JSON.stringify(slice.compound)]
  } else if ({}.hasOwnProperty.call(slice, 'hierarchy')) {
    result = ['hierarchy', JSON.stringify(slice.hierarchy)]
  } else if ({}.hasOwnProperty.call(slice, 'custom')) {
    result = ['custom', JSON.stringify(slice.custom)]
  }

  return result
}

/**
 * Takes an object slice from the schema, along with the dataset, and returns the absolute value,
 * either from the dataset or the schema.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @param {Object} dataset - The dataset to lookup.
 * @returns {[Value, ValueType]}
 */
export const getValue = (slice: any, dataset?: any) => {
  const [type, value] = getValueType(slice)
  if (type === 'valueFrom') {
    return path((value as string).split('.'), dataset)
  }

  return value
}

export const getField = (slice: Contentful.ValueFromType) => getValueType(slice)[1]

export const getDisabledValue = (slice: any, dataset: any) => {
  if (!slice) {
    return false
  }

  return !getValue(slice, dataset)
}

export const getBannerInfo = (header: any) => {
  if (!header.banner || !header.banner.text || !header.banner.linkText) {
    return
  }

  const bannerValues = {
    text: getValue(header.banner.text).trim(),
    linkText: getValue(header.banner.linkText).trim(),
  }

  return bannerValues
}

export const getValidHref = (slice: any, data: any, query: any) => {
  let clickoutHref = getValue(slice?.href, data)
  let ctaText = getValue(slice?.text, data)

  if (Array.isArray(slice)) {
    const { clickoutHrefFromSchema, ctaTextFromSchema } = getLinkAndText(slice, query, data)

    clickoutHref = clickoutHrefFromSchema
    ctaText = ctaTextFromSchema
  }

  return { clickoutHref, ctaText }
}

export interface ListObject {
  label: Object
  value: Object
}

export const getListValues = (product: Object) => (content: ListObject) => {
  return {
    label: getValue(content.label, product),
    value: getValue(content.value, product),
  }
}

export const getTextValues = (product: Object) => (content: Object) => getValue(content, product)

export interface TableCol {
  formatter: string
  value: any
}

export const applyFormat = (col: TableCol) => {
  const format = {
    percentage: (v: number) => percentageFormat(v),
    currency: (v: number) => currencyFormat(v),
  }

  return {
    ...col,
    value: (format[col.formatter] || ((v: number) => v))(col.value),
  }
}

export interface Addon {
  content: any[]
  view: string
}

export const getMoreInformationContent = (addon: Addon, product: Object) => {
  let addonContent = []

  if (addon.view === 'list') {
    addonContent = addon.content
      .map(getListValues(product))
      .filter(({ value }) => value !== null && value !== false)
  } else if (addon.view === 'table') {
    const content: any[] = []

    for (const tableRow of addon.content) {
      if (tableRow.field) {
        const fields = getArrayValue(tableRow.field, product)

        if (fields) {
          for (const field of fields) {
            const row = tableRow.fieldSchema.map((col: TableCol) => {
              return { ...col, value: field[getValue(col.value, product)] }
            })

            content.push(row)
          }
        } else {
          return null
        }
      } else {
        const row = tableRow.data.map((col: TableCol) => {
          return { ...col, value: getValue(col.value, product) }
        })

        content.push(row)
      }
    }

    addonContent = content.map((row) => row.map(applyFormat))
  } else if (addon.view === 'HTML') {
    addonContent = addon.content.map(getTextValues(product))
  }

  if (!addonContent || addonContent?.length === 0 || addonContent.every((chunk) => !chunk)) {
    return null
  }

  return addonContent
}
